import { newId } from '@quickreport/domain';
import { queueChange } from '@quickreport/sync';
import { ImageError, MAX_UPLOAD_BYTES, prepareImage, sha256Hex } from '@quickreport/ui';
import type { LocalDb } from './client';

export { ImageError, MAX_UPLOAD_BYTES };

/**
 * Prepare an uploaded logo or signature and store it locally, queued for sync. Pictures are
 * shrunk and re-encoded (which also drops camera metadata); the same picture is stored once.
 */
export async function storeImage(
  db: LocalDb,
  orgId: string,
  file: File,
  opts: { maxSide?: number; whiteToTransparent?: boolean; jpeg?: boolean } = {},
): Promise<string> {
  return storePrepared(db, orgId, await prepareImage(file, opts));
}

/** Keep a picture that is already prepared (see prepareBackground), queued for sync. */
export async function storePrepared(
  db: LocalDb,
  orgId: string,
  { bytes, mime }: { bytes: Uint8Array; mime: string },
): Promise<string> {
  const sha = await sha256Hex(bytes);
  const existing = await db.query<{ id: string }>(
    'select id from files where org_id = $1 and sha256 = $2 and deleted_at is null limit 1',
    [orgId, sha],
  );
  if (existing.rows[0]) return existing.rows[0].id;

  const id = newId();
  await db.transaction(async (tx) => {
    await tx.query(
      'insert into files (id, org_id, sha256, mime, size) values ($1, $2, $3, $4, $5)',
      [id, orgId, sha, mime, bytes.length],
    );
    await tx.query('insert into file_blobs (id, bytes) values ($1, $2)', [id, bytes]);
    await queueChange(tx, 'files', id);
  });
  return id;
}

export interface StoredImage {
  bytes: Uint8Array;
  mime: string;
}

export async function loadImage(
  db: LocalDb,
  fileId: string | undefined | null,
): Promise<StoredImage | null> {
  if (!fileId) return null;
  const r = await db.query<{ bytes: Uint8Array; mime: string }>(
    'select b.bytes, f.mime from file_blobs b join files f on f.id = b.id where b.id = $1',
    [fileId],
  );
  const row = r.rows[0];
  return row ? { bytes: new Uint8Array(row.bytes), mime: row.mime } : null;
}

export const extensionOf = (mime: string) =>
  mime === 'image/svg+xml'
    ? 'svg'
    : mime === 'image/jpeg'
      ? 'jpg'
      : mime === 'image/webp'
        ? 'webp'
        : 'png';
